/**
 * VisualSection — Reusable collapsible section for the Visual Editor sidebar.
 *
 * Renders a sticky header with an optional "Reset" button and a body slot.
 */
import React from "react";
import { Button, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowResetRegular } from "@fluentui/react-icons";

// ── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  section: {
    marginBottom: "12px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: "0px",
    zIndex: 5,
    height: "28px",
    backgroundColor: tokens.colorNeutralBackground1Hover,
    marginLeft: "-16px",
    marginRight: "-16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
  },
  sectionTitle: {
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontSize: "11px",
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  sectionBody: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    paddingTop: "6px",
  },
});

// ── Props ──────────────────────────────────────────────────────────────────

export interface VisualSectionProps {
  title: string;
  children: React.ReactNode;
  onReset?: () => void;
  isDirty?: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────

const VisualSection: React.FC<VisualSectionProps> = ({
  title,
  children,
  onReset,
  isDirty,
}) => {
  const styles = useStyles();
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>{title}</span>
        {onReset && (
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowResetRegular />}
            onClick={onReset}
            style={{
              visibility: isDirty ? "visible" : "hidden",
              color: tokens.colorBrandForeground1,
              height: "22px",
              padding: "0 6px",
              minWidth: "auto",
            }}
          >
            Reset
          </Button>
        )}
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </div>
  );
};

export default VisualSection;